import {ReactNode, createContext, useMemo, useReducer} from 'react';
import {DatumValue} from './types';

export interface ExportedValues {
  getDatumValue: (inlineDataName: string, datumName: string) => DatumValue;
  setDatumValue: (
    inlineDataName: string,
    datumName: string,
    value: DatumValue,
  ) => void;
}

export const ExportedValuesContext = createContext<ExportedValues>({
  getDatumValue: () => undefined,
  setDatumValue: () => undefined,
});

type ExportedValuesState = Record<string, Record<string, DatumValue>>;

const exportedValuesReducer = (
  state: ExportedValuesState,
  action: {
    inlineDataName: string;
    datumName: string;
    value: DatumValue;
  },
) => {
  const {inlineDataName, datumName, value} = action;
  const newState = {...state};
  if (!newState[inlineDataName]) {
    newState[inlineDataName] = {};
  }
  newState[inlineDataName][datumName] = value;
  return newState;
};

export const ExportedValuesProvider = ({children}: {children: ReactNode}) => {
  const [state, dispatch] = useReducer(exportedValuesReducer, {});

  const getDatumValue = useMemo(
    () => (inlineDataName: string, datumName: string) => {
      return state[inlineDataName]
        ? state[inlineDataName][datumName]
        : undefined;
    },
    [state],
  );

  const setDatumValue = useMemo(
    () => (inlineDataName: string, datumName: string, value: DatumValue) => {
      if (getDatumValue(inlineDataName, datumName) !== value) {
        dispatch({inlineDataName, datumName, value});
      }
    },
    [getDatumValue],
  );

  const value = useMemo(
    () => ({
      getDatumValue,
      setDatumValue,
    }),
    [getDatumValue, setDatumValue],
  );

  return (
    <ExportedValuesContext.Provider value={value}>
      {children}
    </ExportedValuesContext.Provider>
  );
};
